import Button, { buttonVariants, geistSans } from "../../buttons/Button";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Image from "next/image";
import { RichTextField } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";

type CreditProps = {
  variant?: "headingDown" | "default";
  state: "default" | "open";
  name: string;
  image: string;
  description?: RichTextField;
  button?: {
    text: string;
    link?: string;
  };
  className?: string;
  showRightArrow?: boolean;
};

const Credit = ({
  variant = "default",
  state,
  name,
  image,
  description,
  button,
  className,
  showRightArrow = true,
}: CreditProps) => {
  const isOpen = state === "open";
  const isDefault = variant === "default";

  return (
    <article
      className={cn(
        "bg-white-primary shadow-festi flex w-full max-w-[17rem] gap-3 px-3.5 py-2.5",
        geistSans.className,
        variant === "headingDown" ? "flex-col-reverse" : "flex-col",
        isOpen ? "rounded-2xl" : "rounded-full",
        className
      )}
      aria-label={`Credit for photographer ${name}`}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Photographié par</span>
          {image && (
            <figure className="relative h-6.5 w-6.5 overflow-hidden rounded-full">
              <Image src={image} alt={name} fill className="absolute object-cover" />
            </figure>
          )}
          <figcaption className="text-sm font-medium">{name}</figcaption>
        </div>

        {isDefault && showRightArrow && (
          <>
            <hr className="h-5 w-px bg-gray-300" />
            <ArrowDownRight aria-hidden="true" size={20} />
          </>
        )}
      </div>

      {isOpen && (
        <div className="space-y-3">
          {description && (
            <blockquote
              cite="#"
              className={cn(
                "text-xs",
                variant === "headingDown" && "font-cabinet-grotesk text-sm leading-tight font-medium md:text-[17px]"
              )}
            >
              <PrismicRichText field={description} />
            </blockquote>
          )}
          {isDefault &&
            button?.text &&
            (button.link ? (
              <Link
                href={button.link}
                className={cn(buttonVariants({ variant: "rounded", size: "md" }), "w-full text-sm font-medium")}
                aria-label={`Navigate to $  {button.text} page`}
              >
                {button.text}
                <ArrowRight size={20} aria-hidden="true" />
              </Link>
            ) : (
              <Button
                aria-label={`Perform action: ${button.text}`}
                variant="rounded"
                size="md"
                className="w-full text-sm font-medium"
                iconRightName="ArrowRight"
              >
                {button.text}
              </Button>
            ))}
        </div>
      )}
    </article>
  );
};

export default Credit;
